import React, { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { prop } from 'ramda';
import { Link } from '@core/js/components/common/Link';
import { Balance } from '@core/js/components/main/Balance';
import { useComponentTranslations } from '@core/js/hooks/component';
import { useAppSelector, useDeviceSelector } from '@core/js/store/hooks';
import { CaptainUpBalance } from '@theme/js/components/main/CaptainUpBalance';

import styleBalance from '../../MyBalance.scss';
import style from './BalancesDropdown.scss';

export const BalancesDropdown: React.FC<{ isShowCustomBalance: boolean, isBalancesOpen: boolean; closeBalances: () => void }> =
    ({ isShowCustomBalance, isBalancesOpen = false, closeBalances }) => {
        const translate = useComponentTranslations();
        const isDesktop = useDeviceSelector(prop('isDesktop'));
        const balancePage = useAppSelector(prop('balancePage'));
        const ref = useRef<HTMLDivElement>(null);
        const { DTAG_DEFAULT_BALANCE_LIST, DTAG_CUSTOM_BALANCE_LIST } = useAppSelector(prop('dynamicTags'));

        const balanceList = useMemo(() => {
            if (isShowCustomBalance) {
                return JSON?.parse(DTAG_CUSTOM_BALANCE_LIST);
            }
            return JSON?.parse(DTAG_DEFAULT_BALANCE_LIST);
        }, [isShowCustomBalance, DTAG_CUSTOM_BALANCE_LIST, DTAG_DEFAULT_BALANCE_LIST]);

        const handleClickOutside = useCallback((event) => {
            const { current } = ref || {};
            const { target } = event || {};
            let targetClassList = target?.parentNode?.classList;
            if (target?.tagName === 'A') {
                targetClassList = target?.classList;
            }
            if (!current) {
                return;
            }
            if (!current?.contains(target) && !targetClassList?.contains(styleBalance?.balance)) {
                closeBalances();
            }
        }, [closeBalances]);

        useEffect(() => {
            if (!isDesktop || !isBalancesOpen) {
                return;
            }

            document.addEventListener('click', handleClickOutside);

            return () => {
                document.removeEventListener('click', handleClickOutside);
            };
        }, [handleClickOutside, isBalancesOpen, isDesktop]);

        return (
            <div ref={ref} className={`${style.balances} ${isBalancesOpen ? '' : 'hidden'}`}>
                <span className={style.balanceRows}>
                    {balanceList?.map((item: { name: string; type: string; }, index: number) => (
                        <span key={index} className={style.balanceAmount}> {translate(`header.${item?.name}`)}
                            <Suspense fallback="">
                                <Balance type="balance_calc" args={item?.type} />
                            </Suspense>
                        </span>
                    ))}
                    <span className={style.balanceAmount}> {translate('header.captainUpBalance')}
                        <CaptainUpBalance />
                    </span>
                </span>
                <span className={style.balanceLinks}>
                    <Link href={balancePage} className={style.balanceLink} onClick={closeBalances}>
                        {translate('header.myBalances')}
                    </Link>
                    <Link href={'/bonus-history'} className={style.balanceLink} onClick={closeBalances}>
                        {translate('header.myBonuses')}
                    </Link>
                </span>
            </div>
        );
    };
